/**
 * University hub data — per-country grouping of universities with academic calendars.
 * Slug logic lives in slug-university-hub.ts; this module provides data + type only.
 */

import { getCountries, getUniversities, getEventsForUniversity, getMinEventsThreshold } from '@/lib/data-source';
import type { CountryCode, University } from '@/lib/types';

export interface UniversityHub {
  countryCode: CountryCode;
  countryName: string;
  countryFlag: string;
  slug: string;
  lang: string;
  universities: University[];
}

/** Returns raw university hub data grouped by country (English names, no slug). */
export function getUniversityHubsByCountry(): Map<CountryCode, { name: string; flag: string; universities: University[] }> {
  const universities = getUniversities();
  const countries = getCountries();

  const byCountry = new Map<CountryCode, University[]>();
  for (const uni of universities) {
    const list = byCountry.get(uni.country) ?? [];
    list.push(uni);
    byCountry.set(uni.country, list);
  }

  const result = new Map<CountryCode, { name: string; flag: string; universities: University[] }>();
  const threshold = getMinEventsThreshold('university');
  for (const [code, unis] of byCountry) {
    const country = countries.find(c => c.code === code);
    if (!country) continue;
    const filtered = unis.filter(u => getEventsForUniversity(u.id).length >= threshold);
    if (filtered.length === 0) continue;
    result.set(code, { name: country.name, flag: country.flag, universities: filtered });
  }

  return result;
}
